// this component is ported from client v1

import { CollapsibleSection } from '@/app/components/shared/viewer/CollapsibleSection';
import { RapidModel } from '@/types/rapidModel';
import { RawModel } from '@/types/rawModel';
import { Box, Button, Group, NumberInput, Stack, StackProps, Text } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { showErrorNotification } from '../../../../components/shared/notifications';
import { updateRawModel } from '../../../actions/rawModel';
import { MAIN_ASSET_QUERY_KEY } from '../../../hooks/useAssetsQuery';
import { Dimentsions } from '../../atoms/Dimensions';
import { urlHelper } from '@/utils';

interface ScaleComponentProps extends StackProps {
  x?: number;
  y?: number;
  wx?: number;
  wy?: number;
  h?: number;
  asset: RawModel | RapidModel;
}

const getDimensions = (scaleFactor: number, wx: number, wy: number, h: number) => {
  const dimensionX = scaleFactor * wx;
  const dimensionY = scaleFactor * h;
  const dimensionZ = scaleFactor * wy;

  return {
    dimension: { x: dimensionX, y: dimensionY, z: dimensionZ },
    maxDim: Math.max(dimensionX, dimensionY, dimensionZ),
  };
};

export function ScaleComponent({ x = 230, wx = 50, wy = 50, h = 50, asset, ...props }: ScaleComponentProps) {
  const [scaleFactor, setScaleFactor] = useState((asset as RawModel).scale_factor ?? 1.0);
  const queryClient = useQueryClient();
  const { dimension, maxDim } = getDimensions(scaleFactor, wx, wy, h);

  useEffect(() => {
    setScaleFactor((asset as RawModel).scale_factor ?? 1.0);
  }, [asset]);

  const { mutateAsync: changeFactor } = useMutation({
    mutationFn: async (factor: number) => {
      setScaleFactor(factor);

      if (asset.assetType === 'output') {
        return;
      }

      await updateRawModel(asset.id, { scale_factor: factor });
      await queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes(MAIN_ASSET_QUERY_KEY) });

      return factor;
    },
    onError: () => {
      showErrorNotification({
        title: 'Scale factor update failed',
        message: 'Please try again later.',
      });
    },
  });

  const params = new URLSearchParams({
    x: dimension.x.toString(),
    y: dimension.y.toString(),
    z: dimension.z.toString(),
  });

  return (
    <CollapsibleSection title='Scale'>
      <Stack align='flex-start' gap='xs' {...props}>
        <Box my={-24} h={320} w={440} pos='relative' style={{ overflow: 'hidden' }}>
          <iframe
            src={`${urlHelper.scaleWidget()}/index.html?${params.toString()}`}
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              top: 0,
              height: 440,
              width: 620,
              transform: 'scale(0.7)',
              transformOrigin: 'top left',
            }}
          />
        </Box>
        <Text c='gray.6'>Bounding box size</Text>
        <Dimentsions dimension={dimension} />
        {asset.assetType === 'raw' && (
          <>
            <Text fw={600} mt='xs'>
              Scale factor
            </Text>
            <Group gap='xs'>
              <NumberInput
                w={120}
                min={0}
                value={scaleFactor}
                onChange={(v) => changeFactor(parseFloat(v.toString()))}
              />
              <Button variant='default' onClick={() => changeFactor(scaleFactor / 10)}>
                / 10
              </Button>
              <Button variant='default' onClick={() => changeFactor(scaleFactor * 10)}>
                x 10
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </CollapsibleSection>
  );
}
